'use client';

import React, { useEffect, useRef, useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────
interface Project {
  title: string;
  subtitle: string;
  tags: string[];
  description: string;
  url: string;
  featured?: boolean;
  image: string;
  imageAlt: string;
}

interface Skill {
  name: string;
  details: string;
  icon: React.ReactNode;
}

interface Experience {
  period: string;
  location: string;
  role: string;
  company: string;
  description: string;
}

interface Education {
  period: string;
  location: string;
  degree: string;
  institution: string;
  description?: string;
}

interface Certificate {
  title: string;
  issuer: string;
  description: string;
}

// ── Data ───────────────────────────────────────────────────────────────────
const PROJECTS: Project[] = [
{
  title: 'AgroGuard',
  subtitle: 'AI-Powered Agricultural Intelligence Platform',
  tags: ['React', 'Next.js', 'Supabase', 'AI/ML', 'Tailwind CSS'],
  description:
  'AgroGuard is a comprehensive agricultural intelligence platform that leverages AI to help farmers detect crop diseases, monitor soil health, and optimize yields. Built with Next.js and Supabase, it provides real-time data analytics, smart irrigation recommendations, and pest management insights — empowering farmers with technology to grow smarter.',
  url: 'https://www.agroai.online',
  featured: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec37f736-1767642461069.png",
  imageAlt: 'AgroGuard agricultural AI platform showing crop monitoring dashboard with green fields and data analytics'
},
{
  title: 'Peddy',
  subtitle: 'Pet Companion Platform',
  tags: ['Next.js', 'React', 'Tailwind CSS', 'Full-Stack'],
  description:
  'A modern full-stack web application that helps users discover, adopt, and shop for pets and pet products through a responsive and interactive interface. Built with Next.js and React with Tailwind CSS for a fast and visually appealing user experience.',
  url: 'https://peddy-1-htn9.vercel.app/',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_102a48db2-1778244909887.png",
  imageAlt: 'Peddy pet adoption platform showing adorable dogs and cats available for adoption with a clean modern UI'
},
{
  title: 'Gadget Heaven',
  subtitle: 'E-Commerce Web Application',
  tags: ['React', 'Tailwind CSS', 'E-Commerce'],
  description:
  'A responsive e-commerce web application that allows users to browse gadgets, view product details, and manage purchases through cart and wishlist features. Built using React and Tailwind CSS with a component-based architecture for maintainability and scalability.',
  url: 'https://gd-hv-2110.netlify.app/',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef60b78e-1775350382820.png",
  imageAlt: 'Gadget Heaven e-commerce store displaying modern electronics including laptops, phones, and accessories'
},
{
  title: 'Rupaai',
  subtitle: 'Kids Coding & Problem-Solving Platform',
  tags: ['React', 'Gamification', 'EdTech'],
  description:
  'An interactive educational platform designed to improve children\'s logical thinking and problem-solving skills through engaging coding challenges. The platform includes gamified learning features such as challenges, rewards, and leaderboards to motivate users.',
  url: 'https://rupaai-kids-coding.netlify.app/',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12467df98-1778244908213.png",
  imageAlt: 'Rupaai kids coding platform with colorful interactive coding challenges and gamified learning interface for children'
},
{
  title: 'AmazingSpiderman',
  subtitle: 'Marvel Comic Book Explorer',
  tags: ['React', 'Marvel API', 'Vite', 'CSS'],
  description:
  'An interactive Marvel comic book explorer that lets users search for any Marvel character and browse their full comic book library. Powered by the official Marvel API, it features character search, comic listings, and a visually immersive Spider-Man themed UI with custom fonts and background.',
  url: 'https://comic-book-b6d1.vercel.app/',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_2619d4d00-1779292318344.png",
  imageAlt: 'AmazingSpiderman Marvel comic book explorer app showing Spider-Man themed interface with character search and comic listings'
}];


const SKILLS: Skill[] = [
{
  name: 'JavaScript (ES6+)',
  details: 'ES6+, DOM Manipulation, Async/Await, Fetch API, Event Handling, Array Methods, Modular JavaScript',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M0 0h24v24H0V0zm22.034 18.276c-.175-1.095-.888-2.015-3.003-2.873-.736-.345-1.554-.585-1.797-1.14-.091-.33-.105-.51-.046-.705.15-.646.915-.84 1.515-.66.39.12.75.42.976.9 1.034-.676 1.034-.676 1.755-1.125-.27-.42-.404-.601-.586-.78-.63-.705-1.469-1.065-2.834-1.034l-.705.089c-.676.165-1.32.525-1.71 1.005-1.14 1.291-.811 3.541.569 4.471 1.365 1.02 3.361 1.244 3.616 2.205.24 1.17-.87 1.545-1.966 1.41-.811-.18-1.26-.586-1.755-1.336l-1.83 1.051c.21.48.45.689.81 1.109 1.74 1.756 6.09 1.666 6.871-1.004.029-.09.24-.705.074-1.65l.046.067zm-8.983-7.245h-2.248c0 1.938-.009 3.864-.009 5.805 0 1.232.063 2.363-.138 2.711-.33.689-1.18.601-1.566.48-.396-.196-.597-.466-.83-.855-.063-.105-.11-.196-.127-.196l-1.825 1.125c.305.63.75 1.172 1.324 1.517.855.51 2.004.675 3.207.405.783-.226 1.458-.691 1.811-1.411.51-.93.402-2.07.397-3.346.012-2.054 0-4.109 0-6.179l.004-.056z" />
      </svg>

},
{
  name: 'React',
  details: 'Functional Components, Hooks (useState, useEffect), Component Lifecycle, State Management, Props Handling, React Router',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.375.793-1.683 3.264-.973 6.365C1.98 8.917 0 10.42 0 12.004c0 1.59 1.99 3.097 5.043 4.03-.704 3.113-.39 5.588.988 6.38.32.187.69.275 1.102.275 1.345 0 3.107-.96 4.888-2.624 1.78 1.654 3.542 2.603 4.887 2.603.41 0 .783-.09 1.106-.275 1.374-.792 1.683-3.263.973-6.365C22.02 15.096 24 13.59 24 12.004c0-1.59-1.99-3.097-5.043-4.032.704-3.11.39-5.587-.988-6.38-.318-.184-.688-.277-1.092-.278zm-.005 1.09c.225 0 .406.044.558.127.666.382.955 1.835.73 3.704-.054.46-.142.945-.25 1.44-.96-.236-2.006-.417-3.107-.534-.66-.905-1.345-1.727-2.035-2.447 1.592-1.48 3.087-2.292 4.105-2.29zm-9.77.02c1.012 0 2.514.808 4.11 2.28-.686.72-1.37 1.537-2.02 2.442-1.107.117-2.154.298-3.113.538-.112-.49-.195-.964-.254-1.42-.23-1.868.054-3.32.714-3.707.19-.09.4-.127.563-.132zm4.882 3.05c.455.468.91.992 1.36 1.564-.44-.02-.89-.034-1.345-.034-.46 0-.915.01-1.36.034.44-.572.895-1.096 1.345-1.565zM12 8.1c.74 0 1.477.034 2.202.093.406.582.802 1.203 1.183 1.86.372.64.71 1.29 1.018 1.946-.308.655-.646 1.31-1.013 1.95-.38.66-.773 1.288-1.18 1.87-.728.063-1.466.098-2.21.098-.74 0-1.477-.035-2.202-.093-.406-.582-.802-1.204-1.183-1.86-.372-.64-.71-1.29-1.018-1.946.303-.657.646-1.313 1.013-1.954.38-.66.773-1.286 1.18-1.868.728-.064 1.466-.098 2.21-.098zm-3.635.254c-.24.377-.48.763-.704 1.16-.225.39-.435.782-.635 1.174-.265-.656-.49-1.31-.676-1.947.64-.15 1.315-.283 2.015-.386zm7.26 0c.695.103 1.365.23 2.006.387-.18.632-.405 1.282-.66 1.933-.2-.39-.41-.783-.64-1.174-.225-.392-.465-.774-.705-1.146zm3.063.675c.484.15.944.317 1.375.498 1.732.74 2.852 1.708 2.852 2.476-.005.768-1.125 1.74-2.857 2.475-.42.18-.88.342-1.355.493-.28-.958-.646-1.956-1.1-2.98.45-1.017.81-2.01 1.085-2.964zm-13.395.004c.278.96.645 1.957 1.1 2.98-.45 1.017-.812 2.01-1.086 2.964-.484-.15-.944-.318-1.37-.5-1.732-.737-2.852-1.706-2.852-2.474 0-.768 1.12-1.742 2.852-2.476.42-.18.88-.342 1.356-.494zm11.678 4.28c.265.657.49 1.312.676 1.948-.64.157-1.316.29-2.016.39.24-.375.48-.762.705-1.158.225-.39.435-.788.636-1.18zm-9.945.02c.2.392.41.783.64 1.175.23.39.465.772.705 1.143-.695-.102-1.365-.23-2.006-.386.18-.63.406-1.282.66-1.933zM17.92 16.32c.112.493.2.968.254 1.423.23 1.868-.054 3.32-.714 3.708-.147.09-.338.128-.563.128-1.012 0-2.514-.807-4.11-2.28.686-.72 1.37-1.536 2.02-2.44 1.107-.118 2.154-.3 3.113-.54zm-11.83.01c.96.234 2.006.415 3.107.532.66.905 1.345 1.727 2.035 2.446-1.595 1.483-3.092 2.295-4.11 2.295-.22-.005-.406-.05-.553-.132-.666-.38-.955-1.834-.73-3.703.054-.46.142-.944.25-1.438zm4.56.64c.44.02.89.034 1.345.034.46 0 .915-.01 1.36-.034-.44.572-.895 1.095-1.345 1.565-.455-.47-.91-.993-1.36-1.565z" />
      </svg>

},
{
  name: 'Next.js',
  details: 'App Router, Server-Side Rendering (SSR), Static Site Generation (SSG), API Routes, Dynamic Routing, Performance Optimization',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M11.572 0c-.176 0-.31.001-.358.007a19.76 19.76 0 0 1-.364.033C7.443.346 4.25 2.185 2.228 5.012a11.875 11.875 0 0 0-2.119 5.243c-.096.659-.108.854-.108 1.747s.012 1.089.108 1.748c.652 4.506 3.86 8.292 8.209 9.695.779.25 1.6.422 2.534.525.363.04 1.935.04 2.299 0 1.611-.178 2.977-.577 4.323-1.264.207-.106.247-.134.219-.158-.02-.013-.9-1.193-1.955-2.62l-1.919-2.592-2.404-3.558a338.739 338.739 0 0 0-2.422-3.556c-.009-.002-.018 1.579-.023 3.51-.007 3.38-.01 3.515-.052 3.595a.426.426 0 0 1-.206.214c-.075.037-.14.044-.495.044H7.81l-.108-.068a.438.438 0 0 1-.157-.171l-.05-.106.006-4.703.007-4.705.072-.092a.645.645 0 0 1 .174-.143c.096-.047.134-.051.54-.051.478 0 .558.018.682.154.035.038 1.337 1.999 2.895 4.361a10760.433 10760.433 0 0 0 4.735 7.17l1.9 2.879.096-.063a12.317 12.317 0 0 0 2.466-2.163 11.944 11.944 0 0 0 2.824-6.134c.096-.66.108-.854.108-1.748 0-.893-.012-1.088-.108-1.747-.652-4.506-3.859-8.292-8.208-9.695a12.597 12.597 0 0 0-2.499-.523A33.119 33.119 0 0 0 11.573 0zm4.069 7.217c.347 0 .408.005.486.047a.473.473 0 0 1 .237.277c.018.06.023 1.365.018 4.304l-.006 4.218-.744-1.14-.746-1.14v-3.066c0-1.982.01-3.097.023-3.15a.478.478 0 0 1 .233-.296c.096-.05.13-.054.5-.054z" />
      </svg>

},
{
  name: 'Supabase',
  details: 'Authentication, Database Management, API Integration, Realtime Data Handling',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M11.9 1.036c-.015-.986-1.26-1.41-1.874-.637L.764 12.05C.111 12.876.636 14.087 1.59 14.087h9.355v8.878c.015.986 1.26 1.409 1.874.636l9.262-11.653c.653-.826.128-2.036-.826-2.036h-9.355V1.036z" />
      </svg>

},
{
  name: 'Tailwind CSS',
  details: 'Utility-First Styling, Responsive Design, Custom Themes, Component Patterns, Dark Mode',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M12.001 4.8c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624C13.666 10.618 15.027 12 18.001 12c3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C16.337 6.182 14.976 4.8 12.001 4.8zm-6 7.2c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624 1.177 1.194 2.538 2.576 5.512 2.576 3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C10.337 13.382 8.976 12 6.001 12z" />
      </svg>

},
{
  name: 'Git & GitHub',
  details: 'Version Control, Branch Management, Pull Requests, Repository Management',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </svg>

},
{
  name: 'REST API',
  details: 'API Integration, HTTP Methods (GET, POST, PUT, DELETE), JSON Data Handling',
  icon:
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M14 6l-1-2H5v17h2v-7h5l1 2h7V6h-6zm4 8h-4l-1-2H7V6h5l1 2h5v6z" />
      </svg>

}];


const EXPERIENCES: Experience[] = [
{
  period: '02/2024 – 09/2024',
  location: 'Uttara, Dhaka',
  role: 'Junior Front-End Developer',
  company: 'SoftIT Limited',
  description:
  'Built responsive web interfaces using modern JavaScript frameworks. Developed reusable UI components and collaborated with the team to improve performance and user experience. Also worked with APIs and debugging tasks.'
}];


const EDUCATION: Education[] = [
{
  period: '01/2023 – 12/2026',
  location: 'Savar, Bangladesh',
  degree: 'B.Sc. in Software Engineering',
  institution: 'Daffodil International University',
  description:
  'Studying software development, system design, and software testing. Gaining practical knowledge of SDLC, programming, and modern web development through coursework and projects.'
},
{
  period: '01/2019 – 12/2021',
  location: 'Uttara, Dhaka',
  degree: 'HSC',
  institution: 'Nawab Habibullah Model School & College'
},
{
  period: '01/2019 – 12/2019',
  location: 'Uttara, Dhaka',
  degree: 'SSC',
  institution: 'Uttara High School & College'
}];


const CERTIFICATES: Certificate[] = [
{
  title: 'Career Path Exploration with AI',
  issuer: 'Daffodil International University',
  description:
  'Participated in a seminar on Career Path Exploration with AI organized by the Career Development Center at Daffodil International University.'
},
{
  title: 'Emerging Tech: AI & Blockchain for Business',
  issuer: 'Daffodil International University, 2026',
  description:
  'Successfully participated in the Emerging Tech: AI & Blockchain for Business 2026 workshop at Daffodil International University.'
},
{
  title: 'Complete Web Development Certification',
  issuer: 'Programming Hero (03/2023 – 09/2023)',
  description:
  'Completed a comprehensive web development program covering HTML, CSS, JavaScript, and React. Gained hands-on experience building multiple web applications and developing strong frontend development and problem-solving skills.'
}];


const INTERESTS = [
'Sports', 'Photography', 'Videography', 'Volunteering',
'Nature', 'Travel', 'Gaming', 'Entrepreneurship'];


const NAV_LINKS = [
{ label: 'Projects', href: '#projects' },
{ label: 'Skills', href: '#skills' },
{ label: 'About', href: '#about' },
{ label: 'Contact', href: '#contact' }];


const ROTATING_TEXTS = [
'& NEXT.JS',
'& REACT',
'& SUPABASE',
'& TAILWIND'];


// ── Scroll Reveal Hook ─────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ── Custom Cursor ──────────────────────────────────────────────────────────
function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    if (window.matchMedia('(max-width: 768px)').matches) return;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let cx = mx;
    let cy = my;
    let rafId: number;

    const HOVER_SEL = 'a,button,[role="button"],input,textarea,select,label';

    const onMouseMove = (e: MouseEvent) => {
      mx = e.clientX;
      my = e.clientY;
      const t = e.target as Element;
      cursor.classList.remove('hover');
      if (t.closest(HOVER_SEL)) cursor.classList.add('hover');
    };

    const onMouseDown = () => cursor.classList.add('click');
    const onMouseUp = () => cursor.classList.remove('click');

    const tick = () => {
      cx += (mx - cx) * 0.35;
      cy += (my - cy) * 0.35;
      cursor.style.transform = `translate(${cx}px,${cy}px) translate(-50%,-50%)`;
      rafId = requestAnimationFrame(tick);
    };

    document.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onMouseUp);
    rafId = requestAnimationFrame(tick);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return <div ref={cursorRef} className="mridul-cursor" id="mridul-cursor" />;
}

// ── Rotating Text Component ────────────────────────────────────────────────
function RotatingText({ texts }: {texts: string[];}) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % texts.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [texts.length]);

  return (
    <span className="rotating-text-wrapper inline-block min-w-[220px] md:min-w-[300px]">
      <span key={index} className="rotating-text-item gradient-text-rev font-bold">
        {texts[index]}
      </span>
    </span>);

}

// ── Marquee Component ──────────────────────────────────────────────────────
function Marquee({ items }: {items: string[];}) {
  const doubled = [...items, ...items];
  return (
    <div className="marquee-container py-4">
      <ul className="marquee-track">
        {doubled.map((item, i) =>
        <li key={i}>
            <a href="#contact">{item}</a>
          </li>
        )}
      </ul>
    </div>);

}

// ── Interactive Logo Component ─────────────────────────────────────────────
function InteractiveLogo({ size = 'md' }: {size?: 'sm' | 'md';}) {
  const [hovered, setHovered] = useState(false);
  const [rotX, setRotX] = useState(0);
  const [rotY, setRotY] = useState(0);
  const logoRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const autoAngle = useRef(0);

  useEffect(() => {
    if (hovered) return;
    const animate = () => {
      autoAngle.current += 0.4;
      setRotY(autoAngle.current);
      setRotX(Math.sin(autoAngle.current * 0.017) * 8);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [hovered]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!logoRef.current) return;
    const rect = logoRef.current.getBoundingClientRect();
    let cx = rect.left + rect.width / 2;
    let cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    setRotY(dx * 25);
    setRotX(-dy * 25);
  };

  const isSmall = size === 'sm';

  return (
    <div
      ref={logoRef}
      className="relative select-none"
      style={{ perspective: '400px' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {setHovered(false);setRotX(0);setRotY(0);}}
      onMouseMove={handleMouseMove}>
      
      {/* 3D card */}
      <div
        className="relative flex flex-col items-start justify-center"
        style={{
          transform: `rotateX(${rotX}deg) rotateY(${rotY}deg)`,
          transformStyle: 'preserve-3d',
          transition: hovered ? 'transform 0.08s ease-out' : 'none'
        }}>
        
        {/* Floating 3D cube accent */}
        <div
          className="absolute pointer-events-none"
          style={{
            right: isSmall ? '-10px' : '-14px',
            top: isSmall ? '-6px' : '-8px',
            transformStyle: 'preserve-3d',
            transform: `translateZ(8px) rotateX(${rotX * 1.5}deg) rotateY(${rotY * 1.5}deg)`
          }}>
          
          <svg width={isSmall ? '14' : '18'} height={isSmall ? '14' : '18'} viewBox="0 0 18 18" fill="none">
            <polygon points="9,1 17,5 17,13 9,17 1,13 1,5" stroke="#00DEFF" strokeWidth="1" fill="rgba(0,222,255,0.08)" />
            <polygon points="9,1 17,5 9,9 1,5" stroke="#00DEFF" strokeWidth="0.7" fill="rgba(0,222,255,0.12)" />
            <line x1="9" y1="9" x2="9" y2="17" stroke="#00DEFF" strokeWidth="0.7" opacity="0.6" />
          </svg>
        </div>

        {/* "Rupaque Islam" small label */}
        <span
          className="font-roc tracking-widest uppercase"
          style={{
            fontSize: isSmall ? '7px' : '8px',
            color: '#00DEFF',
            letterSpacing: '0.18em',
            lineHeight: 1,
            marginBottom: isSmall ? '1px' : '2px',
            transform: 'translateZ(4px)',
            display: 'block',
            opacity: 0.9
          }}>
          
          Rupaque Islam
        </span>

        {/* MRIDUL main text */}
        <span
          className="font-roc font-bold gradient-text"
          style={{
            fontSize: isSmall ? '14px' : '18px',
            letterSpacing: '0.05em',
            lineHeight: 1,
            transform: 'translateZ(6px)',
            display: 'block',
            textShadow: hovered ? '0 0 20px rgba(0,222,255,0.6)' : '0 0 10px rgba(0,222,255,0.2)',
            transition: 'text-shadow 0.3s ease'
          }}>
          
          MRIDUL
        </span>

        {/* Glow underline */}
        <div
          style={{
            height: '1px',
            width: hovered ? '100%' : '40%',
            background: 'linear-gradient(90deg, #00DEFF, transparent)',
            marginTop: '3px',
            transition: 'width 0.4s ease',
            transform: 'translateZ(2px)'
          }} />
        
      </div>
    </div>);

}

// ── Navbar ─────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sectionIds = NAV_LINKS.map((l) => l.href.replace('#', ''));
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Desktop Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 hidden md:flex items-center justify-between px-8 py-4 transition-all duration-300 animate-fade-in-down ${
        scrolled ? 'nav-backdrop bg-black/60' : 'bg-transparent'}`
        }>
        
        {/* Logo */}
        <div className="flex items-center gap-3">
          <InteractiveLogo size="md" />
        </div>

        {/* Center Nav */}
        <nav className="nav-backdrop bg-white/5 border border-white/10 rounded-full px-6 py-2 flex items-center gap-6">
          {NAV_LINKS.map((link) => {
            const isActive = activeSection === link.href.replace('#', '');
            return (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className={`relative text-sm font-roc transition-colors duration-200 btn-grow pb-0.5 ${
                isActive ? 'text-accent-DEFAULT' : 'text-white/80 hover:text-white'}`
                }>
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 h-px bg-accent-DEFAULT transition-all duration-300 ${
                  isActive ? 'w-full' : 'w-0'}`
                  } />
                
              </button>);

          })}
        </nav>

        {/* Right: Social + CTA */}
        <div className="flex items-center gap-3">
          <a
            href="https://github.com/mridulRahaman01"
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/50 transition-all duration-200 btn-grow">
            
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
            </svg>
          </a>
          <a
            href="https://www.linkedin.com/in/rupaque-islam-mridul-278965318"
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/50 transition-all duration-200 btn-grow">
            
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
          <a
            href="#contact"
            onClick={(e) => {e.preventDefault();handleNavClick('#contact');}}
            className="bg-accent-DEFAULT/10 border border-accent-DEFAULT/40 text-white hover:bg-accent-DEFAULT hover:text-white px-5 py-2 rounded-full text-sm font-roc font-medium transition-all duration-200 btn-grow">
            
            Hire Me
          </a>
        </div>
      </header>

      {/* Mobile Navbar */}
      <header className={`fixed top-0 left-0 right-0 z-50 flex md:hidden items-center justify-between px-5 py-4 transition-all duration-300 ${
      scrolled ? 'nav-backdrop bg-black/60' : 'bg-transparent'}`
      }>
        <div className="flex items-center gap-2">
          <InteractiveLogo size="sm" />
        </div>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-9 h-9 flex flex-col items-center justify-center gap-1.5"
          aria-label="Toggle menu">
          
          <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </header>

      {/* Mobile Menu */}
      {menuOpen &&
      <div className="fixed inset-0 z-40 mobile-menu bg-black/95 flex flex-col items-center justify-center gap-8 md:hidden">
          {NAV_LINKS.map((link) => {
          const isActive = activeSection === link.href.replace('#', '');
          return (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href)}
              className={`text-2xl font-roc font-medium transition-colors ${
              isActive ? 'text-accent-DEFAULT' : 'text-white hover:text-accent-DEFAULT'}`
              }>
                {link.label}
              </button>);

        })}
          <a
          href="#contact"
          onClick={() => {setMenuOpen(false);}}
          className="bg-accent-DEFAULT/10 border border-accent-DEFAULT/40 text-white px-8 py-3 rounded-full text-lg font-roc font-medium mt-4">
          
            Hire Me
          </a>
        </div>
      }
    </>);

}

// ── Hero Section ───────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      
      {/* Animated background grid */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
          'linear-gradient(rgba(0,222,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,222,255,0.15) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

      {/* 3D Floating Tech Logo Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">

        {/* HTML5 Logo — top-left */}
        <div className="absolute top-20 left-10 hidden lg:block tech-logo-float" style={{ animation: 'techFloat1 7s ease-in-out infinite', filter: 'drop-shadow(0 0 10px rgba(228,77,38,0.5))', opacity: 0.55 }}>
          <div className="tech-logo-card" style={{ background: 'rgba(228,77,38,0.08)', border: '1px solid rgba(228,77,38,0.3)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="34" height="34" viewBox="0 0 512 512">
              <path fill="#E44D26" d="M107.644 470.877L74.633 100.62h362.734l-33.046 370.199L255.778 512z" />
              <path fill="#F16529" d="M256 480.523l120.03-33.277 28.24-316.352H256z" />
              <path fill="#EBEBEB" d="M256 268.217h-60.09l-4.15-46.501H256v-45.411H142.132l1.087 12.17 11.161 125.142H256zm0 122.679l-.199.053-50.574-13.656-3.233-36.217h-45.585l6.362 71.301 93.02 25.823.209-.058z" />
              <path fill="#fff" d="M255.843 268.217v45.41h55.932l-5.268 58.894-50.664 13.677v47.244l93.07-25.805.683-7.672 10.671-119.551 1.108-12.197zm0-91.912v45.411h109.569l.911-10.209 2.07-23.021 1.086-12.181z" />
            </svg>
          </div>
        </div>

        {/* CSS3 Logo — top-right */}
        <div className="absolute top-24 right-14 hidden lg:block" style={{ animation: 'techFloat2 9s ease-in-out infinite', filter: 'drop-shadow(0 0 10px rgba(21,114,182,0.5))', opacity: 0.55 }}>
          <div style={{ background: 'rgba(21,114,182,0.08)', border: '1px solid rgba(21,114,182,0.3)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="34" height="34" viewBox="0 0 512 512">
              <path fill="#1572B6" d="M107.644 470.877L74.633 100.62h362.734l-33.046 370.199L255.778 512z" />
              <path fill="#33A9DC" d="M256 480.523l120.03-33.277 28.24-316.352H256z" />
              <path fill="#fff" d="M256 268.217h-60.09l-4.15-46.501H256v-45.411H142.132l1.087 12.17 11.161 125.142H256zm0 122.679l-.199.053-50.574-13.656-3.233-36.217h-45.585l6.362 71.301 93.02 25.823.209-.058z" />
              <path fill="#EBEBEB" d="M255.843 268.217v45.41h55.932l-5.268 58.894-50.664 13.677v47.244l93.07-25.805.683-7.672 10.671-119.551 1.108-12.197zm0-91.912v45.411h109.569l.911-10.209 2.07-23.021 1.086-12.181z" />
            </svg>
          </div>
        </div>

        {/* React.js Logo — left-center */}
        <div className="absolute top-1/3 left-6 hidden lg:block" style={{ animation: 'techFloat3 11s ease-in-out infinite', filter: 'drop-shadow(0 0 10px rgba(97,218,251,0.6))', opacity: 0.55 }}>
          <div style={{ background: 'rgba(97,218,251,0.07)', border: '1px solid rgba(97,218,251,0.3)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="36" height="36" viewBox="0 0 841.9 595.3">
              <g fill="#61DAFB">
                <path d="M666.3 296.5c0-32.5-40.7-63.3-103.1-82.4 14.4-63.6 8-114.2-20.2-130.4-6.5-3.8-14.1-5.6-22.4-5.6v22.3c4.6 0 8.3.9 11.4 2.6 13.6 7.8 19.5 37.5 14.9 75.7-1.1 9.4-2.9 19.3-5.1 29.4-19.6-4.8-41-8.5-63.5-10.9-13.5-18.5-27.5-35.3-41.6-50 32.6-30.3 63.2-46.9 84-46.9V78c-27.5 0-63.5 19.6-99.9 53.6-36.4-33.8-72.4-53.2-99.9-53.2v22.3c20.7 0 51.4 16.5 84 46.6-14 14.7-28 31.4-41.3 49.9-22.6 2.4-44 6.1-63.6 11-2.3-10-4-19.7-5.2-29-4.7-38.2 1.1-67.9 14.6-75.8 3-1.8 6.9-2.6 11.5-2.6V78.5c-8.4 0-16 1.8-22.6 5.6-28.1 16.2-34.4 66.7-19.9 130.1-62.2 19.2-102.7 49.9-102.7 82.3 0 32.5 40.7 63.3 103.1 82.4-14.4 63.6-8 114.2 20.2 130.4 6.5 3.8 14.1 5.6 22.5 5.6 27.5 0 63.5-19.6 99.9-53.6 36.4 33.8 72.4 53.2 99.9 53.2 8.4 0 16-1.8 22.6-5.6 28.1-16.2 34.4-66.7 19.9-130.1 62-19.1 102.5-49.9 102.5-82.3zm-130.2-66.7c-3.7 12.9-8.3 26.2-13.5 39.5-4.1-8-8.4-16-13.1-24-4.6-8-9.5-15.8-14.4-23.4 14.2 2.1 27.9 4.7 41 7.9zm-45.8 106.5c-7.8 13.5-15.8 26.3-24.1 38.2-14.9 1.3-30 2-45.2 2-15.1 0-30.2-.7-45-1.9-8.3-11.9-16.4-24.6-24.2-38-7.6-13.1-14.5-26.4-20.8-39.8 6.2-13.4 13.2-26.8 20.7-39.9 7.8-13.5 15.8-26.3 24.1-38.2 14.9-1.3 30-2 45.2-2 15.1 0 30.2.7 45 1.9 8.3 11.9 16.4 24.6 24.2 38 7.6 13.1 14.5 26.4 20.8 39.8-6.3 13.4-13.2 26.8-20.7 39.9zm32.3-13c5.4 13.4 10 26.8 13.8 39.8-13.1 3.2-26.9 5.9-41.2 8 4.9-7.7 9.8-15.6 14.4-23.7 4.6-8 8.9-16.1 13-24.1zM421.2 430c-9.3-9.6-18.6-20.3-27.8-32 9 .4 18.2.7 27.5.7 9.4 0 18.7-.2 27.8-.7-9 11.7-18.3 22.4-27.5 32zm-74.4-58.9c-14.2-2.1-27.9-4.7-41-7.9 3.7-12.9 8.3-26.2 13.5-39.5 4.1 8 8.4 16 13.1 24 4.7 8 9.5 15.8 14.4 23.4zM420.7 163c9.3 9.6 18.6 20.3 27.8 32-9-.4-18.2-.7-27.5-.7-9.4 0-18.7.2-27.8.7 9-11.7 18.3-22.4 27.5-32zm-74 58.9c-4.9 7.7-9.8 15.6-14.4 23.7-4.6 8-8.9 16.1-13 24.1-5.4-13.4-10-26.8-13.8-39.8 13.1-3.1 26.9-5.8 41.2-7.9zm-90.5 125.2c-35.4-15.1-58.3-34.9-58.3-50.6 0-15.7 22.9-35.6 58.3-50.6 8.6-3.7 18-7 27.7-10.1 5.7 19.6 13.2 40 22.5 60.9-9.2 20.8-16.6 41.1-22.2 60.6-9.9-3.1-19.3-6.5-28-10.2zM310 490c-13.6-7.8-19.5-37.5-14.9-75.7 1.1-9.4 2.9-19.3 5.1-29.4 19.6 4.8 41 8.5 63.5 10.9 13.5 18.5 27.5 35.3 41.6 50-32.6 30.3-63.2 46.9-84 46.9-4.5-.1-8.3-1-11.3-2.7zm237.2-76.2c4.7 38.2-1.1 67.9-14.6 75.8-3 1.8-6.9 2.6-11.5 2.6-20.7 0-51.4-16.5-84-46.6 14-14.7 28-31.4 41.3-49.9 22.6-2.4 44-6.1 63.6-11 2.3 10.1 4.1 19.8 5.2 29.1zm38.5-66.7c-8.6 3.7-18 7-27.7 10.1-5.7-19.6-13.2-40-22.5-60.9 9.2-20.8 16.6-41.1 22.2-60.6 9.9 3.1 19.3 6.5 28.1 10.2 35.4 15.1 58.3 34.9 58.3 50.6-.1 15.7-23 35.6-58.4 50.6z" />
                <circle cx="420.9" cy="296.5" r="45.7" />
              </g>
            </svg>
          </div>
        </div>

        {/* Next.js Logo — right-center */}
        <div className="absolute top-2/5 right-8 hidden xl:block" style={{ animation: 'techFloat1 10s ease-in-out infinite 1s', filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.35))', opacity: 0.55 }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="36" height="36" viewBox="0 0 180 180" fill="none">
              <mask id="nxmask" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="180" height="180">
                <circle cx="90" cy="90" r="90" fill="black" />
              </mask>
              <g mask="url(#nxmask)">
                <circle cx="90" cy="90" r="90" fill="black" />
                <path d="M149.508 157.52L69.142 54H54V125.97H66.1V69.3L139.999 164.845C143.333 162.614 146.509 160.165 149.508 157.52Z" fill="url(#nxgrad1)" />
                <rect x="115" y="54" width="12" height="72" fill="url(#nxgrad2)" />
              </g>
              <defs>
                <linearGradient id="nxgrad1" x1="109" y1="116.5" x2="144.5" y2="160.5" gradientUnits="userSpaceOnUse">
                  <stop stopColor="white" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="nxgrad2" x1="115" y1="54" x2="115.5" y2="106.5" gradientUnits="userSpaceOnUse">
                  <stop stopColor="white" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Node.js Logo — bottom-left */}
        <div className="absolute bottom-28 left-12 hidden md:block" style={{ animation: 'techFloat2 8s ease-in-out infinite 0.5s', filter: 'drop-shadow(0 0 10px rgba(104,160,99,0.6))', opacity: 0.55 }}>
          <div style={{ background: 'rgba(104,160,99,0.08)', border: '1px solid rgba(104,160,99,0.3)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="36" height="36" viewBox="0 0 256 289">
              <path fill="#539E43" d="M128 288.464c-3.975 0-7.685-1.06-11.13-2.915l-35.247-20.936c-5.3-2.915-2.65-3.975-1.06-4.505 7.155-2.385 8.48-2.915 15.9-7.155.796-.53 1.856-.265 2.65.265l27.032 16.166c1.06.53 2.385.53 3.18 0l105.74-61.217c1.06-.53 1.59-1.59 1.59-2.915V83.08c0-1.325-.53-2.385-1.59-2.915L128.53 19.948c-1.06-.53-2.385-.53-3.18 0L19.61 81.165c-1.06.53-1.59 1.855-1.59 2.915v122.17c0 1.06.53 2.385 1.59 2.915l28.887 16.695c15.635 7.95 25.44-1.325 25.44-10.6V94.21c0-1.59 1.325-2.915 2.915-2.915h12.455c1.59 0 2.915 1.325 2.915 2.915V215.26c0 20.936-11.395 32.86-31.27 32.86-6.095 0-10.865 0-24.38-6.625L9.54 224.74C3.71 221.56 0 215.465 0 208.84V86.67c0-6.625 3.71-12.72 9.54-15.9L119.255 9.348c5.565-3.18 13.25-3.18 18.815 0L247.785 70.77c5.83 3.18 9.54 9.275 9.54 15.9V208.84c0 6.625-3.71 12.72-9.54 15.9l-109.45 63.34c-3.18 1.59-6.89 2.385-10.335 2.385z" />
              <path fill="#539E43" d="M161.32 207.78c-46.285 0-55.825-21.2-55.825-39.22 0-1.59 1.325-2.915 2.915-2.915h12.72c1.325 0 2.65 1.06 2.65 2.385 1.855 12.455 7.42 18.55 37.54 18.55 23.32 0 33.125-5.3 33.125-17.49 0-7.155-2.65-12.19-38.07-15.635-29.415-2.915-47.61-9.54-47.61-33.39 0-22.26 18.815-35.51 50.085-35.51 35.245 0 52.47 12.19 54.59 38.335 0 .795-.265 1.59-.795 2.12-.53.53-1.325.795-2.12.795h-12.72c-1.325 0-2.385-.795-2.65-2.12-3.445-15.105-11.66-19.875-36.305-19.875-26.77 0-29.945 9.275-29.945 16.165 0 8.48 3.71 10.865 36.84 15.635 33.125 4.77 48.76 11.395 48.76 33.125.265 24.115-20.14 37.54-53.13 37.54z" />
            </svg>
          </div>
        </div>

        {/* Supabase Logo — bottom-right */}
        <div className="absolute bottom-24 right-16 hidden md:block" style={{ animation: 'techFloat3 9s ease-in-out infinite 1.5s', filter: 'drop-shadow(0 0 10px rgba(62,207,142,0.6))', opacity: 0.55 }}>
          <div style={{ background: 'rgba(62,207,142,0.07)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="36" height="36" viewBox="0 0 109 113" fill="none">
              <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fill="url(#sbgrad1)" />
              <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fill="url(#sbgrad2)" fillOpacity="0.2" />
              <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.04075L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z" fill="#3ECF8E" />
              <defs>
                <linearGradient id="sbgrad1" x1="53.9738" y1="54.974" x2="94.1635" y2="71.8295" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#249361" />
                  <stop offset="1" stopColor="#3ECF8E" />
                </linearGradient>
                <linearGradient id="sbgrad2" x1="36.1558" y1="30.578" x2="54.4844" y2="65.0806" gradientUnits="userSpaceOnUse">
                  <stop />
                  <stop offset="1" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Django Logo — top-center-right */}
        <div className="absolute top-36 right-1/3 hidden lg:block" style={{ animation: 'techFloat1 12s ease-in-out infinite 2s', filter: 'drop-shadow(0 0 10px rgba(9,150,70,0.5))', opacity: 0.55 }}>
          <div style={{ background: 'rgba(9,150,70,0.07)', border: '1px solid rgba(9,150,70,0.3)', borderRadius: '14px', padding: '8px', backdropFilter: 'blur(6px)' }}>
            <svg width="34" height="34" viewBox="0 0 256 256" fill="none">
              <rect width="256" height="256" rx="20" fill="#092E20" />
              <path d="M114.784 50H143.31V185.779C128.459 188.67 117.286 189.908 104.694 189.908C67.8958 189.908 48.5 171.454 48.5 135.871C48.5 101.513 69.1251 80.2373 102.397 80.2373C107.84 80.2373 111.862 80.8579 114.784 82.0991V50ZM114.784 107.663C112.481 106.834 109.868 106.213 106.016 106.213C90.3361 106.213 81.2539 116.469 81.2539 135.457C81.2539 154.031 90.0264 163.874 105.706 163.874C109.248 163.874 112.171 163.667 114.784 163.046V107.663Z" fill="white" />
              <path d="M175.25 50H203.776V80.8579H175.25V50ZM175.25 83.7495H203.776V188.256H175.25V83.7495Z" fill="white" />
            </svg>
          </div>
        </div>

        {/* Floating glow orbs behind logos */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full opacity-8 blur-3xl" style={{ background: 'radial-gradient(circle, #00DEFF, transparent)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-56 h-56 rounded-full opacity-8 blur-3xl" style={{ background: 'radial-gradient(circle, #3ECF8E, transparent)' }} />
      </div>

      {/* Social icons left */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-4 z-10">
        {[
        {
          href: 'https://github.com/mridulRahaman01',
          label: 'GitHub',
          icon:
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
              </svg>

        },
        {
          href: 'https://www.linkedin.com/in/rupaque-islam-mridul-278965318',
          label: 'LinkedIn',
          icon:
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>

        },
        {
          href: 'mailto:mridulrahaman430@gmail.com',
          label: 'Email',
          icon:
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>

        }].
        map((social) =>
        <a
          key={social.label}
          href={social.href}
          target={social.href.startsWith('http') ? '_blank' : undefined}
          rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
          aria-label={social.label}
          className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/50 transition-all duration-200 btn-grow">
          
            {social.icon}
          </a>
        )}
      </div>

      {/* Hero Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Rotating subtitle */}
        <div className="mb-4 animate-fade-in" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
          <h2 className="text-white/70 text-sm md:text-base font-roc tracking-[0.3em] uppercase">
            MERN STACK{' '}
            <RotatingText texts={ROTATING_TEXTS} />
          </h2>
        </div>

        {/* Main Name */}
        <div className="animate-fade-in" style={{ animationDelay: '0.15s', animationFillMode: 'both' }}>
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-roc font-bold tracking-tight leading-none mb-6">
            <span className="gradient-text">MRIDUL</span>
          </h1>
        </div>

        {/* Description */}
        <div className="animate-fade-in" style={{ animationDelay: '0.25s', animationFillMode: 'both' }}>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-10 font-roc">
            I build clean, scalable web applications. From responsive interfaces to full-stack solutions,
            I help bring digital ideas to life with modern technologies.
          </p>
        </div>

        {/* CTA */}
        <div className="animate-fade-in flex flex-col sm:flex-row gap-4 justify-center" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
          <a
            href="#projects"
            onClick={(e) => {e.preventDefault();document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });}}
            className="bg-accent-DEFAULT/10 border border-accent-DEFAULT/50 text-white hover:bg-accent-DEFAULT hover:text-white px-8 py-3 rounded-full text-sm font-roc font-medium transition-all duration-300 btn-grow">
            
            View Projects
          </a>
          <a
            href="#contact"
            onClick={(e) => {e.preventDefault();document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });}}
            className="border border-white/20 text-white hover:border-white/50 hover:text-white px-8 py-3 rounded-full text-sm font-roc font-medium transition-all duration-300 btn-grow">
            
            Get In Touch
          </a>
          <a
            href="https://drive.google.com/file/d/1pMHM09IzILtv2EaPNuRU7BV2eb8DLRBN/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 border border-accent-DEFAULT/30 text-white hover:border-accent-DEFAULT hover:text-white px-8 py-3 rounded-full text-sm font-roc font-medium transition-all duration-300 btn-grow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0">
              <path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download CV
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/30 text-xs font-roc tracking-widest uppercase">Scroll</span>
          <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-accent-DEFAULT rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </section>);

}

// ── Projects Section ───────────────────────────────────────────────────────
function ProjectsSection() {
  return (
    <section id="projects" className="py-24 bg-black">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 reveal">
          <div>
            <h3 className="text-accent-DEFAULT/70 text-sm font-roc tracking-widest uppercase mb-2">Portfolio</h3>
            <h2 className="text-white text-3xl md:text-4xl font-roc font-semibold">Featured Projects</h2>
          </div>
          <a
            href="https://github.com/mridulRahaman01"
            target="_blank"
            rel="noopener noreferrer"
            className="all-projects-btn btn-grow hidden sm:inline-flex">
            All Projects
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* AgroGuard Featured (Full Width) */}
        <div className="mb-10 reveal">
          <a
            href={PROJECTS[0].url}
            target="_blank"
            rel="noopener noreferrer"
            className="project-card featured-badge block rounded-3xl overflow-hidden p-1 group">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={PROJECTS[0].image}
                alt={PROJECTS[0].imageAlt}
                className="w-full h-64 md:h-96 object-cover group-hover:scale-105 transition-transform duration-500" />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
              {/* Featured badge */}
              <div className="absolute top-4 left-4">
                <span className="bg-accent-DEFAULT/20 border border-accent-DEFAULT/60 text-accent-DEFAULT text-xs font-roc font-bold px-3 py-1.5 rounded-full tracking-widest uppercase shadow-lg shadow-accent-DEFAULT/20">
                  ★ Featured Project
                </span>
              </div>
              {/* External link icon */}
              <div className="absolute top-4 right-4">
                <div className="w-9 h-9 rounded-full bg-black/60 border border-white/30 flex items-center justify-center group-hover:border-accent-DEFAULT/60 group-hover:bg-accent-DEFAULT/10 transition-all duration-300">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-white group-hover:text-accent-DEFAULT transition-colors">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </div>
              </div>
              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex flex-wrap gap-2 mb-3">
                  {PROJECTS[0].tags.map((tag) =>
                  <span key={tag} className="tag-badge">{tag}</span>
                  )}
                </div>
                <h3 className="text-white text-2xl md:text-3xl font-roc font-semibold mb-1">{PROJECTS[0].title}</h3>
                <p className="text-accent-DEFAULT/80 text-sm font-roc">{PROJECTS[0].subtitle}</p>
              </div>
            </div>
            <div className="p-6 flex items-center justify-between gap-4">
              <p className="text-white/60 text-sm font-roc leading-relaxed line-clamp-2 flex-1">{PROJECTS[0].description}</p>
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 text-accent-DEFAULT text-xs font-roc font-semibold border border-accent-DEFAULT/40 bg-accent-DEFAULT/10 px-4 py-2 rounded-full whitespace-nowrap">
                View Live
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </a>
        </div>

        {/* Other Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROJECTS.slice(1).map((project, i) =>
          <div key={project.title} className="reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
              <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="project-card-grid block rounded-3xl overflow-hidden group h-full flex flex-col">
                <div className="relative overflow-hidden">
                  <img
                  src={project.image}
                  alt={project.imageAlt}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-3 right-3">
                    <div className="w-8 h-8 rounded-full bg-black/60 border border-white/25 flex items-center justify-center group-hover:border-accent-DEFAULT/60 group-hover:bg-accent-DEFAULT/10 transition-all duration-300">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-white group-hover:text-accent-DEFAULT transition-colors">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </div>
                  </div>
                  {/* Number badge */}
                  <div className="absolute bottom-3 left-3">
                    <span className="text-white/30 text-xs font-roc font-bold tracking-widest">0{i + 2}</span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {project.tags.slice(0, 3).map((tag) =>
                  <span key={tag} className="tag-badge text-xs">{tag}</span>
                  )}
                  </div>
                  <h3 className="text-white text-lg font-roc font-semibold mb-1 group-hover:text-accent-DEFAULT transition-colors duration-300">{project.title}</h3>
                  <p className="text-accent-DEFAULT/60 text-xs font-roc mb-3">{project.subtitle}</p>
                  <p className="text-white/50 text-xs font-roc leading-relaxed line-clamp-3 flex-1">{project.description}</p>
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                    <span className="text-white/30 text-xs font-roc">View Project</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-accent-DEFAULT/60 group-hover:text-accent-DEFAULT group-hover:translate-x-1 transition-all duration-300">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </a>
            </div>
          )}
        </div>

        {/* Mobile All Projects link */}
        <div className="mt-8 flex justify-center sm:hidden reveal">
          <a
            href="https://github.com/mridulRahaman01"
            target="_blank"
            rel="noopener noreferrer"
            className="all-projects-btn btn-grow">
            All Projects
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>);

}

// ── Skills Statement ───────────────────────────────────────────────────────
function SkillsStatement() {
  return (
    <section className="py-24 bg-black border-t border-white/5">
      <div className="max-w-5xl mx-auto px-6">
        <div className="reveal">
          <h2 className="text-white text-2xl md:text-4xl lg:text-5xl font-roc font-semibold leading-tight">
            Equipped with expertise in{' '}
            <span className="gradient-text">front-end development</span>{' '}and{' '}
            <span className="gradient-text">full-stack engineering,</span>{' '}I build{' '}
            <span className="gradient-text">modern, scalable applications</span>{' '}that are clean, performant, and user-focused.
          </h2>
        </div>
        <div className="mt-10 reveal">
          <a
            href="#contact"
            onClick={(e) => {e.preventDefault();document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });}}
            className="inline-flex items-center gap-2 bg-accent-DEFAULT/10 border border-accent-DEFAULT/40 text-white hover:bg-accent-DEFAULT hover:text-white px-6 py-3 rounded-full text-sm font-roc font-medium transition-all duration-300 btn-grow">
            
            Let&apos;s Work Together
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>);

}

// ── Skills Section ─────────────────────────────────────────────────────────
function SkillsSection() {
  return (
    <section id="skills" className="py-24 bg-black border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between mb-12 reveal">
          <div>
            <h3 className="text-white/40 text-sm font-roc tracking-widest uppercase mb-2">Expertise</h3>
            <h2 className="text-white text-3xl md:text-4xl font-roc font-semibold">My Skills</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {SKILLS.map((skill, i) =>
          <div
            key={skill.name}
            className="skill-card reveal bg-dark-card border border-dark-border rounded-2xl p-6"
            style={{ transitionDelay: `${i * 0.05}s` }}>
            
              <div className="text-accent-DEFAULT mb-4">{skill.icon}</div>
              <h3 className="text-white font-roc font-semibold text-base mb-2">{skill.name}</h3>
              <p className="text-white/40 text-xs font-roc leading-relaxed">{skill.details}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}

// ── About Section ──────────────────────────────────────────────────────────
function AboutSection() {
  return (
    <section id="about" className="py-24 bg-black border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6">

        {/* Section Header */}
        <div className="reveal mb-16 text-center">
          <h3 className="text-white/30 text-xs font-roc tracking-[0.3em] uppercase mb-3">Get To Know Me</h3>
          <h2 className="text-white text-4xl md:text-5xl font-roc font-semibold">About <span className="text-accent-DEFAULT">Me</span></h2>
          <div className="glow-line mx-auto mt-4" style={{ width: '60px' }} />
        </div>

        {/* Top: Photo + Bio */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mb-12">

          {/* Photo Column */}
          <div className="lg:col-span-2 reveal flex flex-col items-center lg:items-start">
            <div className="relative group">
              {/* Outer glow ring */}
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-accent-DEFAULT via-accent-DEFAULT/30 to-transparent opacity-60 blur-sm group-hover:opacity-90 transition-opacity duration-500" />
              {/* Corner accent */}
              <div className="absolute -top-3 -right-3 w-16 h-16 border-t-2 border-r-2 border-accent-DEFAULT rounded-tr-xl opacity-70" />
              <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b-2 border-l-2 border-accent-DEFAULT rounded-bl-xl opacity-70" />
              {/* Photo */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ width: '280px', height: '340px' }}>
                <img
                  src="/assets/images/WhatsApp_Image_2026-04-05_at_2.55.17_AM-1778245018245.jpeg"
                  alt="Rupaque Islam Mridul — Software Engineer and Web Developer"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                
                {/* Subtle gradient overlay at bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                {/* Name tag at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-roc font-semibold text-base leading-tight">Rupaque Islam Mridul</p>
                  <p className="text-accent-DEFAULT text-xs font-roc tracking-wider mt-0.5">Software Engineer</p>
                </div>
              </div>
            </div>

            {/* Social / quick links below photo */}
            <div className="mt-6 flex gap-3">
              <a
                href="https://github.com/mridulRahaman01"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-card border border-dark-border text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/40 transition-all duration-300 text-xs font-roc">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                </svg>
                GitHub
              </a>
              <a
                href="mailto:mridulrahaman430@gmail.com"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-card border border-dark-border text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/40 transition-all duration-300 text-xs font-roc">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </a>
              <a
                href="https://www.linkedin.com/in/rupaque-islam-mridul-278965318"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-card border border-dark-border text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/40 transition-all duration-300 text-xs font-roc">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                LinkedIn
              </a>
            </div>
          </div>

          {/* Bio Column */}
          <div className="lg:col-span-3 reveal flex flex-col justify-center">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-DEFAULT/10 border border-accent-DEFAULT/20 mb-4">
                <span className="w-2 h-2 rounded-full bg-accent-DEFAULT animate-pulse" />
                <span className="text-accent-DEFAULT text-xs font-roc tracking-wider">Available for opportunities</span>
              </div>
              <h3 className="text-white text-2xl md:text-3xl font-roc font-semibold mb-4 leading-snug">
                Building digital experiences<br />
                <span className="text-white/40">with clean, scalable code</span>
              </h3>
              <p className="text-white/55 font-roc text-sm leading-relaxed mb-4">
                I&apos;m a Software Engineering student at Daffodil International University with hands-on experience in web development
                and software quality assurance. I specialize in building modern, responsive web applications using React, Next.js,
                Tailwind CSS, and Supabase.
              </p>
              <p className="text-white/55 font-roc text-sm leading-relaxed">
                I also have experience in manual testing, bug reporting, and issue tracking using Jira. I focus on developing clean,
                scalable applications while maintaining high software quality and usability — thriving in collaborative environments
                where attention to detail and problem-solving matter.
              </p>
            </div>

            {/* Contact Info Grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
              { label: 'Email', value: 'mridulrahaman430@gmail.com', href: 'mailto:mridulrahaman430@gmail.com', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
              { label: 'Phone', value: '01747381040', href: 'tel:01747381040', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
              { label: 'Location', value: 'Uttara, Dhaka', href: null, icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
              { label: 'GitHub', value: 'mridulRahaman01', href: 'https://github.com/mridulRahaman01', icon: 'M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z' },
              { label: 'LinkedIn', value: 'rupaque-islam-mridul', href: 'https://www.linkedin.com/in/rupaque-islam-mridul-278965318', icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
              { label: 'CV / Resume', value: 'View & Download', href: 'https://drive.google.com/file/d/1pMHM09IzILtv2EaPNuRU7BV2eb8DLRBN/view?usp=sharing', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }].
              map((info) =>
              <div key={info.label} className="bg-dark-card border border-dark-border rounded-xl p-4 hover:border-accent-DEFAULT/30 transition-colors duration-300 group">
                  <div className="flex items-center gap-2 mb-1.5">
                    <svg viewBox="0 0 24 24" fill={info.label === 'GitHub' || info.label === 'LinkedIn' ? 'currentColor' : 'none'} stroke={info.label === 'GitHub' || info.label === 'LinkedIn' ? 'none' : 'currentColor'} strokeWidth="1.5" className="w-3.5 h-3.5 text-accent-DEFAULT flex-shrink-0">
                      <path d={info.icon} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="text-white/30 text-xs font-roc uppercase tracking-wider">{info.label}</p>
                  </div>
                  {info.href ?
                <a
                  href={info.href}
                  target={info.href.startsWith('http') ? '_blank' : undefined}
                  rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="text-white/70 text-xs font-roc group-hover:text-accent-DEFAULT transition-colors truncate block">
                      {info.value}
                    </a> :
                <p className="text-white/70 text-xs font-roc">{info.value}</p>
                }
                </div>
              )}
            </div>

            {/* Interests */}
            <div className="mt-6">
              <h3 className="text-white/30 text-xs font-roc tracking-widest uppercase mb-3">Interests</h3>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((interest) =>
                <span key={interest} className="interest-tag text-xs font-roc">{interest}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: Experience & Education side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Experience */}
          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-accent-DEFAULT/10 border border-accent-DEFAULT/20 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent-DEFAULT">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="text-white font-roc font-semibold text-base tracking-wide">Experience</h3>
            </div>
            <div className="relative pl-5">
              <div className="timeline-line" />
              {EXPERIENCES.map((exp, i) =>
              <div key={i} className="relative mb-6 last:mb-0">
                  <div className="absolute -left-5 top-1.5 flex items-center justify-center">
                    <div className="timeline-dot" />
                  </div>
                  <div className="bg-dark-card border border-dark-border rounded-xl p-4 hover:border-accent-DEFAULT/25 transition-colors duration-300">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="text-accent-DEFAULT text-xs font-roc">{exp.period}</span>
                      <span className="text-white/20 text-xs">·</span>
                      <span className="text-white/40 text-xs font-roc">{exp.location}</span>
                    </div>
                    <h4 className="text-white font-roc font-semibold text-sm mb-0.5">{exp.role}</h4>
                    <p className="text-accent-DEFAULT/80 text-xs font-roc mb-2">{exp.company}</p>
                    <p className="text-white/45 text-xs font-roc leading-relaxed">{exp.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Education */}
          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-accent-DEFAULT/10 border border-accent-DEFAULT/20 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent-DEFAULT">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                </svg>
              </div>
              <h3 className="text-white font-roc font-semibold text-base tracking-wide">Education</h3>
            </div>
            <div className="relative pl-5">
              <div className="timeline-line" />
              {EDUCATION.map((edu, i) =>
              <div key={i} className="relative mb-6 last:mb-0">
                  <div className="absolute -left-5 top-1.5 flex items-center justify-center">
                    <div className="timeline-dot" />
                  </div>
                  <div className="bg-dark-card border border-dark-border rounded-xl p-4 hover:border-accent-DEFAULT/25 transition-colors duration-300">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="text-accent-DEFAULT text-xs font-roc">{edu.period}</span>
                      <span className="text-white/20 text-xs">·</span>
                      <span className="text-white/40 text-xs font-roc">{edu.location}</span>
                    </div>
                    <h4 className="text-white font-roc font-semibold text-sm mb-0.5">{edu.degree}</h4>
                    <p className="text-accent-DEFAULT/80 text-xs font-roc mb-2">{edu.institution}</p>
                    {edu.description &&
                  <p className="text-white/40 text-xs font-roc leading-relaxed">{edu.description}</p>
                  }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </section>);

}

// ── Certificates Section ───────────────────────────────────────────────────
function CertificatesSection() {
  return (
    <section className="py-24 bg-black border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6">
        <div className="reveal mb-12">
          <h3 className="text-white/40 text-sm font-roc tracking-widest uppercase mb-2">Achievements</h3>
          <h2 className="text-white text-3xl md:text-4xl font-roc font-semibold">Certificates & Courses</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CERTIFICATES.map((cert, i) =>
          <div
            key={cert.title}
            className="skill-card reveal bg-dark-card border border-dark-border rounded-2xl p-6"
            style={{ transitionDelay: `${i * 0.1}s` }}>
            
              <div className="w-10 h-10 rounded-full bg-accent-DEFAULT/10 border border-accent-DEFAULT/30 flex items-center justify-center mb-4">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-accent-DEFAULT">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                </svg>
              </div>
              <h3 className="text-white font-roc font-semibold text-base mb-1">{cert.title}</h3>
              <p className="text-accent-DEFAULT/70 text-xs font-roc mb-3">{cert.issuer}</p>
              <p className="text-white/40 text-xs font-roc leading-relaxed">{cert.description}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}

// ── Contact Section ────────────────────────────────────────────────────────
function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="py-24 bg-black border-t border-white/5">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center reveal mb-16">
          <h3 className="text-white/40 text-sm font-roc tracking-widest uppercase mb-2">Get In Touch</h3>
          <h2 className="text-white text-3xl md:text-5xl font-roc font-semibold mb-4">
            Ready to build something{' '}
            <span className="gradient-text">great?</span>
          </h2>
          <p className="text-white/50 font-roc text-base max-w-xl mx-auto">
            Tell me about your project and let&apos;s work together to create something amazing.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="reveal">
            <div className="space-y-6">
              {[
              {
                icon:
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>,

                label: 'Email',
                value: 'mridulrahaman430@gmail.com',
                href: 'mailto:mridulrahaman430@gmail.com'
              },
              {
                icon:
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                    </svg>,

                label: 'Phone',
                value: '01747381040',
                href: 'tel:01747381040'
              },
              {
                icon:
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>,

                label: 'Location',
                value: 'Uttara, Dhaka, Bangladesh',
                href: null
              }].
              map((item) =>
              <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent-DEFAULT/10 border border-accent-DEFAULT/30 flex items-center justify-center text-accent-DEFAULT flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-white/30 text-xs font-roc uppercase tracking-wider mb-0.5">{item.label}</p>
                    {item.href ?
                  <a href={item.href} className="text-white/80 text-sm font-roc hover:text-accent-DEFAULT transition-colors">
                        {item.value}
                      </a> :

                  <p className="text-white/80 text-sm font-roc">{item.value}</p>
                  }
                  </div>
                </div>
              )}
            </div>

            {/* Social Links */}
            <div className="mt-10">
              <p className="text-white/30 text-xs font-roc uppercase tracking-wider mb-4">Find me on</p>
              <div className="flex gap-3">
                {[
                { href: 'https://github.com/mridulRahaman01', label: 'GitHub', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg> },
                { href: 'https://www.linkedin.com/in/rupaque-islam-mridul-278965318', label: 'LinkedIn', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg> },
                { href: 'mailto:mridulrahaman430@gmail.com', label: 'Email', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg> }].
                map((social) =>
                <a
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith('http') ? '_blank' : undefined}
                  rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:text-accent-DEFAULT hover:border-accent-DEFAULT/50 transition-all duration-200 btn-grow">
                  
                    {social.icon}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="reveal">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-white/40 text-xs font-roc uppercase tracking-wider mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  required
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm font-roc placeholder-white/20 focus:outline-none focus:border-accent-DEFAULT/50 transition-colors" />
                
              </div>
              <div>
                <label className="block text-white/40 text-xs font-roc uppercase tracking-wider mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm font-roc placeholder-white/20 focus:outline-none focus:border-accent-DEFAULT/50 transition-colors" />
                
              </div>
              <div>
                <label className="block text-white/40 text-xs font-roc uppercase tracking-wider mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell me about your project..."
                  required
                  rows={5}
                  className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white text-sm font-roc placeholder-white/20 focus:outline-none focus:border-accent-DEFAULT/50 transition-colors resize-none" />
                
              </div>
              <button
                type="submit"
                className="w-full bg-accent-DEFAULT/10 border border-accent-DEFAULT/40 text-white hover:bg-accent-DEFAULT hover:text-white py-3 rounded-xl text-sm font-roc font-medium transition-all duration-300 btn-grow">
                
                {submitted ? '✓ Message Sent!' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>);

}

// ── Footer ─────────────────────────────────────────────────────────────────
function Footer() {
  const MARQUEE_ITEMS = [
  "Ready to build something great?",
  "Let\'s work together!",
  "Open to new opportunities.",
  "Let\'s work together!",
  "Available for freelance projects.",
  "Let\'s work together!",
  "Let\'s create something amazing.",
  "Let\'s work together!"];


  const [year, setYear] = useState(2025);

  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  return (
    <footer className="relative bg-black border-t border-white/5 overflow-hidden">
      {/* Marquee */}
      <div className="py-6 border-b border-white/5">
        <Marquee items={MARQUEE_ITEMS} />
      </div>

      {/* CTA */}
      <div className="relative py-24 px-6 text-center overflow-hidden">
        {/* Background glow */}
        <div
          className="absolute inset-0 opacity-5"
          style={{ background: 'radial-gradient(ellipse at center, #00DEFF, transparent 70%)' }} />
        
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="text-white text-3xl md:text-5xl font-roc font-semibold mb-4">
            Ready to build something great?
          </h2>
          <p className="text-white/50 font-roc text-base mb-8">
            Let&apos;s talk about your ideas and build something amazing together.
          </p>
          <a
            href="mailto:mridulrahaman430@gmail.com"
            className="inline-flex items-center gap-2 bg-accent-DEFAULT/10 border border-accent-DEFAULT/40 text-white hover:bg-accent-DEFAULT hover:text-white px-8 py-4 rounded-full text-base font-roc font-medium transition-all duration-300 btn-grow">
            
            Start a Conversation
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-white/30 text-xs font-roc">
          &copy;{year}. Mridul Rahman. All rights reserved.
        </p>
        <div className="flex items-center gap-4">
          <a href="mailto:mridulrahaman430@gmail.com" className="text-white/30 text-xs font-roc hover:text-accent-DEFAULT transition-colors">
            mridulrahaman430@gmail.com
          </a>
          <span className="text-white/10">&middot;</span>
          <a href="https://github.com/mridulRahaman01" target="_blank" rel="noopener noreferrer" className="text-white/30 text-xs font-roc hover:text-accent-DEFAULT transition-colors">
            GitHub
          </a>
        </div>
      </div>
    </footer>);

}

// ── Scroll To Top ──────────────────────────────────────────────────────────
function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="scroll-top-btn"
      aria-label="Scroll to top">
      
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-accent-DEFAULT">
        <path d="M18 15l-6-6-6 6" />
      </svg>
    </button>);

}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  useScrollReveal();

  return (
    <main className="bg-black min-h-screen font-roc">
      <CustomCursor />
      <Navbar />
      <HeroSection />
      <ProjectsSection />
      <SkillsStatement />
      <SkillsSection />
      <AboutSection />
      <CertificatesSection />
      <ContactSection />
      <Footer />
      <ScrollToTop />
    </main>);

}